// features/dashboard/DashboardPage.tsx
/**
 * Main Dashboard — Executive, high-performance real-time business overview.
 * Crafted with crisp light-mode aesthetics, rich emerald & teal accents, and deep operational insights.
 */

import { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Badge } from '@/components/ui/Badge';
import { ErrorState } from '@/components/ui/ErrorState';
import { useAuthStore } from '@/store/authStore';
import apiClient from '@/lib/api-client';
import { getBranches } from '@/features/settings/services/settingsApi';
import { CashierDashboard } from './CashierDashboard';
import {
    DollarSign,
    ShoppingCart,
    Users,
    CreditCard,
    Banknote,
    Smartphone,
    BarChart3,
    Activity,
    RefreshCw,
    Calendar,
    CheckCircle2,
    Sparkles,
    ChevronRight,
    Zap,
    Tag,
    Receipt,
    AlertCircle,
    Building2,
    Monitor,
    TrendingUp,
    TrendingDown,
    Utensils,
    PlusCircle,
    ArrowRightLeft,
    FileText,
    Clock,
    Flame,
    Layers,
    Package,
    ArrowUpRight,
    AlertTriangle,
} from 'lucide-react';
import { clsx } from 'clsx';

// ============================================================
// HELPERS
// ============================================================

const formatNaira = (val: string | number): string => {
    const num = typeof val === 'string' ? parseFloat(val || '0') : val;
    if (isNaN(num)) return '₦0.00';
    if (num >= 1_000_000) return `₦${(num / 1_000_000).toFixed(2)}M`;
    if (num >= 1_000) return `₦${(num / 1_000).toFixed(1)}K`;
    return `₦${num.toLocaleString('en-NG', { minimumFractionDigits: 2 })}`;
};

const formatFullNaira = (val: string | number): string => {
    const num = typeof val === 'string' ? parseFloat(val || '0') : val;
    if (isNaN(num)) return '₦0.00';
    return `₦${num.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const getGreeting = (name: string) => {
    const hour = new Date().getHours();
    if (hour < 12) return `Good Morning, ${name}`;
    if (hour < 17) return `Good Afternoon, ${name}`;
    return `Good Evening, ${name}`;
};

const getGreetingEmoji = () => {
    const hour = new Date().getHours();
    if (hour < 12) return '☀️';
    if (hour < 17) return '🌤️';
    return '🌙';
};

// ============================================================
// SUB-COMPONENTS
// ============================================================

function MetricKpiCard({
    label,
    value,
    fullValue,
    icon: Icon,
    gradient,
    subtitle,
    trend,
    trendValue,
    onClick,
    accent,
    badge,
}: {
    label: string;
    value: string;
    fullValue?: string;
    icon: React.ElementType;
    gradient: string;
    subtitle?: string;
    trend?: 'up' | 'down' | 'neutral';
    trendValue?: string;
    onClick?: () => void;
    accent?: string;
    badge?: string;
}) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            whileHover={onClick ? { y: -3 } : undefined}
            onClick={onClick}
            className={clsx(
                'relative overflow-hidden bg-white rounded-2xl border border-slate-200/90 shadow-sm p-5 hover:shadow-md hover:border-slate-300 transition-all duration-200 group',
                onClick && 'cursor-pointer'
            )}
        >
            {/* Ambient accent top border */}
            <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${gradient}`} />

            <div className="flex items-start justify-between mb-3">
                <div className={`p-3 rounded-xl bg-gradient-to-br ${gradient} shadow-sm group-hover:scale-105 transition-transform`}>
                    <Icon className="w-5 h-5 text-white" />
                </div>
                <div className="flex items-center gap-1.5">
                    {badge && (
                        <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
                            {badge}
                        </span>
                    )}
                    {onClick && (
                        <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-emerald-600 group-hover:translate-x-0.5 transition-all" />
                    )}
                </div>
            </div>

            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                {label}
            </p>

            <div className="flex items-baseline gap-2 mt-1">
                <p
                    title={fullValue}
                    className={clsx(
                        'text-2xl lg:text-3xl font-extrabold tracking-tight',
                        accent || 'text-slate-900'
                    )}
                >
                    {value}
                </p>
            </div>

            <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-100">
                {subtitle && (
                    <p className="text-xs text-slate-500 truncate max-w-[160px]">
                        {subtitle}
                    </p>
                )}
                {trend && trendValue && (
                    <div
                        className={clsx(
                            'inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-md ml-auto',
                            trend === 'up' && 'text-emerald-700 bg-emerald-50',
                            trend === 'down' && 'text-rose-700 bg-rose-50',
                            trend === 'neutral' && 'text-slate-600 bg-slate-100'
                        )}
                    >
                        {trend === 'up' && <TrendingUp size={12} />}
                        {trend === 'down' && <TrendingDown size={12} />}
                        <span>{trendValue}</span>
                    </div>
                )}
            </div>
        </motion.div>
    );
}

function CircularProgressCard({
    label,
    value,
    subValue,
    icon: Icon,
    gradient,
    progress = 100,
    onClick,
    accent,
}: {
    label: string;
    value: string | number;
    subValue?: string;
    icon: React.ElementType;
    gradient: string;
    progress?: number;
    onClick?: () => void;
    accent?: string;
}) {
    const radius = 22;
    const circumference = 2 * Math.PI * radius;
    const clampedProgress = Math.min(100, Math.max(0, progress));
    const offset = circumference - (clampedProgress / 100) * circumference;
    const id = `ring-${label.replace(/[^a-zA-Z0-9]/g, '-')}`;

    return (
        <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            whileHover={onClick ? { y: -2 } : undefined}
            onClick={onClick}
            className={clsx(
                'bg-white rounded-2xl border border-slate-200/90 shadow-sm p-4 hover:shadow-md transition-all duration-200 group flex items-center justify-between',
                onClick && 'cursor-pointer'
            )}
        >
            <div className="flex items-center gap-3.5">
                <div className="relative w-14 h-14 flex-shrink-0">
                    <svg className="w-14 h-14 -rotate-90" viewBox="0 0 56 56">
                        <circle
                            cx="28"
                            cy="28"
                            r={radius}
                            fill="none"
                            stroke="currentColor"
                            className="text-slate-100"
                            strokeWidth="4.5"
                        />
                        <motion.circle
                            cx="28"
                            cy="28"
                            r={radius}
                            fill="none"
                            stroke={`url(#${id})`}
                            strokeWidth="4.5"
                            strokeLinecap="round"
                            initial={{ strokeDashoffset: circumference }}
                            animate={{ strokeDashoffset: offset }}
                            transition={{ duration: 1.0, ease: 'easeOut' }}
                            strokeDasharray={circumference}
                        />
                        <defs>
                            <linearGradient id={id} x1="0%" y1="0%" x2="100%" y2="0%">
                                <stop offset="0%" stopColor="#059669" />
                                <stop offset="100%" stopColor="#0d9488" />
                            </linearGradient>
                        </defs>
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                        <div className={`p-1.5 rounded-full bg-gradient-to-br ${gradient} shadow-sm`}>
                            <Icon className="w-3.5 h-3.5 text-white" />
                        </div>
                    </div>
                </div>
                <div>
                    <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                        {label}
                    </p>
                    <p className={clsx('text-lg font-extrabold mt-0.5', accent || 'text-slate-900')}>
                        {value}
                    </p>
                    {subValue && (
                        <p className="text-xs text-slate-400 mt-0.5">{subValue}</p>
                    )}
                </div>
            </div>
            {onClick && (
                <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-emerald-600 group-hover:translate-x-0.5 transition-all" />
            )}
        </motion.div>
    );
}

function PaymentChannelRow({
    method,
    icon: Icon,
    total,
    grandTotal,
    count,
    color,
}: {
    method: string;
    icon: React.ElementType;
    total: number;
    grandTotal: number;
    count: number;
    color: string;
}) {
    const pct = grandTotal > 0 ? Math.round((total / grandTotal) * 100) : 0;
    const methodNames: Record<string, string> = {
        cash: 'Cash In Hand',
        pos_card: 'POS Card Terminal',
        bank_transfer: 'Bank Transfer / Wire',
        split: 'Split Payment',
    };

    return (
        <div className="p-3.5 rounded-xl border border-slate-100 bg-slate-50/70 hover:bg-slate-100/80 transition-colors">
            <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2.5">
                    <div className="p-2 rounded-lg bg-white shadow-sm border border-slate-200/50">
                        <Icon size={16} className={color} />
                    </div>
                    <div>
                        <span className="text-sm font-semibold text-slate-800">
                            {methodNames[method] || method.replace(/_/g, ' ')}
                        </span>
                        <span className="text-xs text-slate-400 block">
                            {count} {count === 1 ? 'sale' : 'sales'}
                        </span>
                    </div>
                </div>
                <div className="text-right">
                    <span className="text-sm font-bold text-slate-900 block">
                        {formatFullNaira(total)}
                    </span>
                    <span className="text-xs font-semibold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded">
                        {pct}%
                    </span>
                </div>
            </div>
            <div className="w-full bg-slate-200/80 rounded-full h-2 overflow-hidden">
                <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${pct}%` }}
                    transition={{ duration: 0.8, ease: 'easeOut' }}
                    className={clsx('h-2 rounded-full', color.replace('text-', 'bg-'))}
                />
            </div>
        </div>
    );
}

// ============================================================
// MAIN COMPONENT
// ============================================================

export function DashboardPage() {
    const user = useAuthStore((s) => s.user);
    const permissions = useAuthStore((s) => s.permissions);
    const hasRole = useAuthStore((s) => s.hasRole);
    const navigate = useNavigate();

    const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
    const [branchId, setBranchId] = useState('');
    const [refreshing, setRefreshing] = useState(false);
    const [showPosSelector, setShowPosSelector] = useState(false);
    const [activeHourlyBar, setActiveHourlyBar] = useState<number | null>(null);

    const tenant = useAuthStore((s) => s.tenant);

    // If platform owner visits tenant dashboard, redirect to platform command center
    useEffect(() => {
        const SYSTEM_TENANT_ID = import.meta.env.VITE_SYSTEM_TENANT_ID || 'system';
        const isPlatform =
            (user as any)?.isPlatformAdmin ||
            tenant?.slug === 'system' ||
            user?.tenantId === SYSTEM_TENANT_ID ||
            user?.tenantId === '49a95754-da93-49ff-91a5-cd12dc082d28' ||
            user?.email?.toLowerCase() === 'inyangpsalms@gmail.com' ||
            tenant?.name?.toLowerCase().includes('system platform') ||
            tenant?.name?.toLowerCase().includes('etipos system') ||
            localStorage.getItem('tenantSlug') === 'system';
        if (isPlatform && !localStorage.getItem('originalPlatformAccessToken')) {
            navigate('/super-admin', { replace: true });
        }
    }, [tenant, user, navigate]);

    // Permission checks
    const isSuperAdmin = useMemo(() => hasRole('Super Admin'), [hasRole]);

    // Cashier isolation check — isolates cashiers so they only see their personal station & drawer metrics
    const isCashierOnly = useMemo(() => {
        const isAdmin = hasRole('Admin') || hasRole('Super Admin') || hasRole('Owner');
        const isManager = hasRole('Manager');
        const hasPos = hasRole('Cashier') || user?.posAccess === 'retail';
        return hasPos && !isAdmin && !isManager && !permissions.includes('reports:view') && !permissions.includes('*');
    }, [hasRole, user?.posAccess, permissions]);

    const canViewReports = useMemo(
        () => isSuperAdmin || permissions.includes('reports:view') || permissions.includes('*'),
        [isSuperAdmin, permissions]
    );

    const canViewStaff = useMemo(
        () => isSuperAdmin || permissions.includes('users:view') || permissions.includes('*'),
        [isSuperAdmin, permissions]
    );

    const canViewTerminals = useMemo(
        () => isSuperAdmin || permissions.includes('pos:access') || permissions.includes('terminals:view') || permissions.includes('*'),
        [isSuperAdmin, permissions]
    );

    const canViewBranches = useMemo(
        () => isSuperAdmin || permissions.includes('branches:view') || permissions.includes('*'),
        [isSuperAdmin, permissions]
    );

    const posAccess = user?.posAccess || 'none';

    const handleOpenPos = () => {
        if (posAccess === 'bom') {
            navigate('/bom-pos');
        } else if (posAccess === 'both' || isSuperAdmin) {
            setShowPosSelector(true);
        } else {
            navigate('/pos');
        }
    };

    // Queries
    const { data: branches } = useQuery({
        queryKey: ['branches-dash', user?.tenantId],
        queryFn: () => getBranches(user!.tenantId),
        enabled: !!user?.tenantId && !isCashierOnly,
        staleTime: 5 * 60 * 1000,
    });

    const {
        data: dashboard,
        isLoading,
        isError,
        refetch,
    } = useQuery({
        queryKey: ['dashboard', user?.tenantId, date, branchId],
        queryFn: async () => {
            const { data } = await apiClient.get(
                `/tenants/${user!.tenantId}/reports/sales-dashboard`,
                {
                    params: {
                        date,
                        branchId: branchId || undefined,
                    },
                }
            );
            return data.data;
        },
        enabled: !!user?.tenantId && !isCashierOnly && canViewReports,
        refetchInterval: 30_000,
    });

    const { data: staffData } = useQuery({
        queryKey: ['staff-dash', user?.tenantId],
        queryFn: async () => {
            const { data } = await apiClient.get(`/tenants/${user!.tenantId}/users`);
            return data;
        },
        enabled: !!user?.tenantId && !isCashierOnly && canViewStaff,
        staleTime: 5 * 60 * 1000,
    });

    const { data: terminals } = useQuery({
        queryKey: ['pos-terminals-dash', user?.tenantId],
        queryFn: async () => {
            const { data } = await apiClient.get(`/tenants/${user!.tenantId}/pos-terminals`);
            return data.data || [];
        },
        enabled: !!user?.tenantId && !isCashierOnly && canViewTerminals,
        staleTime: 5 * 60 * 1000,
    });

    const handleRefresh = async () => {
        setRefreshing(true);
        await refetch();
        setTimeout(() => setRefreshing(false), 500);
    };

    // Derived values
    const salesTrend = dashboard?.salesTrend ? parseFloat(dashboard.salesTrend) : 0;
    const grandTotal = parseFloat(dashboard?.totalSales || '0');
    const hasData = dashboard && dashboard.totalTransactions > 0;

    const branchList = Array.isArray(branches) ? branches : [];
    const activeBranches = branchList.filter((b: any) => b.isActive).length;
    const totalBranches = branchList.length;
    const branchHealth = totalBranches > 0 ? (activeBranches / totalBranches) * 100 : 0;

    const staffList = staffData?.data?.users || staffData?.data || [];
    const staffArray = Array.isArray(staffList) ? staffList : [];
    const activeStaff = staffArray.filter((s: any) => s.isActive).length;
    const totalStaff = staffArray.length;
    const staffActive = totalStaff > 0 ? (activeStaff / totalStaff) * 100 : 0;

    const terminalList = Array.isArray(terminals) ? terminals : [];
    const onlineTerminals = terminalList.filter(
        (t: any) =>
            t.lastSeenAt &&
            new Date(t.lastSeenAt).getTime() > Date.now() - 10 * 60 * 1000
    ).length;
    const activeTerminals = terminalList.filter((t: any) => t.isActive).length;
    const terminalOnline = activeTerminals > 0 ? (onlineTerminals / activeTerminals) * 100 : 0;

    const todayDate = new Date().toLocaleDateString('en-NG', {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        year: 'numeric',
    });

    // Peak hourly sale calculations
    const hourlySales: Array<{ hour: number; revenue: number; count: number }> =
        dashboard?.hourlySales || [];
    const maxHourRevenue = useMemo(() => {
        return Math.max(1, ...hourlySales.map((h) => h.revenue));
    }, [hourlySales]);
    const peakHour = useMemo(() => {
        if (!hourlySales.length) return null;
        return [...hourlySales].sort((a, b) => b.revenue - a.revenue)[0];
    }, [hourlySales]);

    // Quick date shortcuts
    const setToday = () => setDate(new Date().toISOString().slice(0, 10));
    const setYesterday = () => {
        const d = new Date();
        d.setDate(d.getDate() - 1);
        setDate(d.toISOString().slice(0, 10));
    };

    if (isCashierOnly) {
        return <CashierDashboard />;
    }

    if (isLoading && canViewReports) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[75vh]">
                <div className="relative w-16 h-16 mb-4">
                    <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1.5, repeat: Infinity, ease: 'linear' }}
                        className="w-16 h-16 rounded-full border-4 border-slate-200 border-t-emerald-600"
                    />
                    <div className="absolute inset-0 flex items-center justify-center">
                        <Zap className="w-6 h-6 text-emerald-600 animate-pulse" />
                    </div>
                </div>
                <h3 className="text-base font-semibold text-slate-800">Synchronizing Dashboard</h3>
                <p className="text-sm text-slate-400 mt-1">Aggregating live sales and branch metrics...</p>
            </div>
        );
    }

    if (isError && canViewReports) {
        return (
            <div className="p-6 max-w-4xl mx-auto">
                <ErrorState
                    title="Failed to Load Live Dashboard"
                    message="An error occurred while connecting to the reporting service. Please check your connection."
                    onRetry={() => refetch()}
                />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50/60 pb-16">
            <div className="max-w-[1600px] mx-auto p-4 lg:p-7 space-y-6">
                
                {/* 1. HERO BANNER */}
                <motion.div
                    initial={{ opacity: 0, y: -15 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="relative overflow-hidden bg-gradient-to-r from-emerald-700 via-teal-800 to-slate-900 rounded-3xl shadow-xl border border-emerald-600/30 text-white"
                >
                    {/* Ambient light patterns */}
                    <div className="absolute inset-0 opacity-15 pointer-events-none">
                        <div className="absolute -top-24 -right-24 w-96 h-96 bg-emerald-400 rounded-full blur-3xl" />
                        <div className="absolute -bottom-20 -left-20 w-80 h-80 bg-teal-300 rounded-full blur-3xl" />
                    </div>

                    <div className="relative px-6 py-6 lg:px-8 lg:py-7">
                        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
                            
                            {/* User greeting */}
                            <div className="flex items-center gap-4">
                                <div className="relative flex-shrink-0">
                                    <div className="w-16 h-16 lg:w-18 lg:h-18 rounded-2xl bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center shadow-lg">
                                        <Zap className="w-8 h-8 text-emerald-300" />
                                    </div>
                                    <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-emerald-400 rounded-full ring-2 ring-emerald-900 flex items-center justify-center">
                                        <Sparkles className="w-3 h-3 text-slate-900" />
                                    </div>
                                </div>
                                <div>
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-200 border border-emerald-400/30">
                                            {todayDate}
                                        </span>
                                        <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-white/10 text-white/90">
                                            {branchId ? (branchList.find((b: any) => b.id === branchId)?.name || 'Branch') : 'All Branches'}
                                        </span>
                                    </div>
                                    <h1 className="text-2xl lg:text-3xl font-extrabold text-white mt-1.5 tracking-tight">
                                        {getGreetingEmoji()} {getGreeting(user?.firstName || 'Business Leader')}
                                    </h1>
                                    <p className="text-emerald-100/80 text-xs lg:text-sm mt-0.5">
                                        {user?.email || 'Live operational overview'}
                                    </p>
                                </div>
                            </div>

                            {/* Actions & Live Status */}
                            <div className="flex items-center gap-3 w-full lg:w-auto justify-end flex-wrap">
                                <button
                                    onClick={handleRefresh}
                                    disabled={refreshing}
                                    className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/10 hover:bg-white/15 text-white border border-white/20 text-xs font-semibold backdrop-blur-sm transition-all"
                                    type="button"
                                >
                                    <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
                                    <span>Sync</span>
                                </button>

                                <button
                                    onClick={handleOpenPos}
                                    className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-white text-emerald-800 hover:bg-emerald-50 text-xs font-bold shadow-lg transition-all transform active:scale-95"
                                    type="button"
                                >
                                    <ShoppingCart size={15} className="text-emerald-600" />
                                    <span>Open POS Register</span>
                                    <ChevronRight size={13} className="text-emerald-500" />
                                </button>
                            </div>
                        </div>
                    </div>
                </motion.div>

                {/* 2. QUICK ACTION LAUNCHER (NEW) */}
                <div className="bg-white rounded-2xl border border-slate-200/90 shadow-sm p-3.5">
                    <div className="flex items-center justify-between gap-3 overflow-x-auto pb-1 scrollbar-none">
                        <span className="text-xs font-bold text-slate-400 uppercase tracking-wider pl-2 flex items-center gap-1.5 flex-shrink-0">
                            <Layers size={13} />
                            Quick Actions:
                        </span>

                        <div className="flex items-center gap-2 flex-nowrap">
                            <button
                                onClick={handleOpenPos}
                                className="flex items-center gap-2 px-3.5 py-1.5 rounded-xl text-xs font-semibold bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200 transition-colors whitespace-nowrap"
                                type="button"
                            >
                                <ShoppingCart size={13} />
                                New POS Sale
                            </button>

                            <button
                                onClick={() => navigate('/products/new')}
                                className="flex items-center gap-2 px-3.5 py-1.5 rounded-xl text-xs font-semibold bg-emerald-600 text-white hover:bg-emerald-700 shadow-sm transition-colors whitespace-nowrap"
                                type="button"
                            >
                                <PlusCircle size={13} />
                                Add Product
                            </button>

                            <button
                                onClick={() => navigate('/products')}
                                className="flex items-center gap-2 px-3.5 py-1.5 rounded-xl text-xs font-semibold bg-slate-50 text-slate-700 hover:bg-slate-100 border border-slate-200 transition-colors whitespace-nowrap"
                                type="button"
                            >
                                <Package size={13} className="text-slate-500" />
                                View Products
                            </button>

                            <button
                                onClick={() => navigate('/inventory/stock-levels')}
                                className="flex items-center gap-2 px-3.5 py-1.5 rounded-xl text-xs font-semibold bg-slate-50 text-slate-700 hover:bg-slate-100 border border-slate-200 transition-colors whitespace-nowrap"
                                type="button"
                            >
                                <Layers size={13} className="text-slate-500" />
                                Stock Levels
                            </button>

                            <button
                                onClick={() => navigate('/inventory/transfers')}
                                className="flex items-center gap-2 px-3.5 py-1.5 rounded-xl text-xs font-semibold bg-slate-50 text-slate-700 hover:bg-slate-100 border border-slate-200 transition-colors whitespace-nowrap"
                                type="button"
                            >
                                <ArrowRightLeft size={13} className="text-slate-500" />
                                Stock Transfer
                            </button>

                            <button
                                onClick={() => navigate('/sales')}
                                className="flex items-center gap-2 px-3.5 py-1.5 rounded-xl text-xs font-semibold bg-slate-50 text-slate-700 hover:bg-slate-100 border border-slate-200 transition-colors whitespace-nowrap"
                                type="button"
                            >
                                <Receipt size={13} className="text-slate-500" />
                                Sales Orders
                            </button>

                            {canViewReports && (
                                <button
                                    onClick={() => navigate('/reports')}
                                    className="flex items-center gap-2 px-3.5 py-1.5 rounded-xl text-xs font-semibold bg-slate-50 text-slate-700 hover:bg-slate-100 border border-slate-200 transition-colors whitespace-nowrap"
                                    type="button"
                                >
                                    <FileText size={13} className="text-slate-500" />
                                    Detailed Reports
                                </button>
                            )}

                            {canViewBranches && (
                                <button
                                    onClick={() => navigate('/branches')}
                                    className="flex items-center gap-2 px-3.5 py-1.5 rounded-xl text-xs font-semibold bg-slate-50 text-slate-700 hover:bg-slate-100 border border-slate-200 transition-colors whitespace-nowrap"
                                    type="button"
                                >
                                    <Building2 size={13} className="text-slate-500" />
                                    Branches
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* 3. FILTERS & TIMEFRAME CONTROLS */}
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
                    <div className="flex items-center gap-2 flex-wrap">
                        {/* Date Picker */}
                        <div className="flex items-center gap-2 px-3.5 py-2 bg-white border border-slate-200 rounded-xl shadow-sm">
                            <Calendar size={14} className="text-emerald-600" />
                            <input
                                type="date"
                                value={date}
                                onChange={(e) => setDate(e.target.value)}
                                className="border-none bg-transparent text-xs font-medium text-slate-700 focus:ring-0 p-0 cursor-pointer"
                            />
                        </div>

                        {/* Quick Day Switchers */}
                        <div className="flex items-center gap-1 bg-white border border-slate-200 p-1 rounded-xl shadow-sm">
                            <button
                                onClick={setToday}
                                className={clsx(
                                    'px-2.5 py-1 rounded-lg text-xs font-semibold transition-all',
                                    date === new Date().toISOString().slice(0, 10)
                                        ? 'bg-emerald-600 text-white shadow-sm'
                                        : 'text-slate-600 hover:bg-slate-100'
                                )}
                                type="button"
                            >
                                Today
                            </button>
                            <button
                                onClick={setYesterday}
                                className="px-2.5 py-1 rounded-lg text-xs font-semibold text-slate-600 hover:bg-slate-100 transition-all"
                                type="button"
                            >
                                Yesterday
                            </button>
                        </div>

                        {/* Branch Selector */}
                        <select
                            value={branchId}
                            onChange={(e) => setBranchId(e.target.value)}
                            className="px-3 py-2 border border-slate-200 rounded-xl text-xs font-medium bg-white text-slate-700 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 shadow-sm"
                        >
                            <option value="">All Branches ({branchList.length})</option>
                            {branchList.map((b: any) => (
                                <option key={b.id} value={b.id}>
                                    {b.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="flex items-center gap-2">
                        {hasData ? (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                                Live POS Sync Active
                            </span>
                        ) : (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-600 border border-slate-200">
                                <AlertCircle size={12} />
                                No transactions on this date
                            </span>
                        )}
                    </div>
                </div>

                {/* 4. LOW STOCK ALERT BANNER (IF ANY) */}
                {dashboard?.lowStockCount && dashboard.lowStockCount > 0 ? (
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-amber-50/80 border border-amber-200/90 rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 shadow-sm"
                    >
                        <div className="flex items-center gap-3">
                            <div className="p-2.5 rounded-xl bg-amber-100 text-amber-700 flex-shrink-0">
                                <AlertTriangle className="w-5 h-5" />
                            </div>
                            <div>
                                <h4 className="text-xs font-bold text-amber-900 flex items-center gap-2">
                                    Inventory Warning: {dashboard.lowStockCount} product{dashboard.lowStockCount > 1 ? 's are' : ' is'} below minimum threshold
                                    <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-200/60 text-amber-800">
                                        Reorder Recommended
                                    </span>
                                </h4>
                                <p className="text-[11px] text-amber-700/90 mt-0.5">
                                    Items have dropped at or below their low-stock thresholds across branches. Replenishment prevents stockouts at checkout.
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                            <button
                                onClick={() => navigate('/inventory/stock-levels')}
                                className="px-3.5 py-1.5 rounded-xl bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold shadow-sm transition-colors flex items-center gap-1.5"
                                type="button"
                            >
                                <Layers size={13} />
                                Inspect Stock Levels
                            </button>
                        </div>
                    </motion.div>
                ) : null}

                {/* 5. PRIMARY SALES KPI METRICS */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <MetricKpiCard
                        label="Today's Total Sales"
                        value={formatNaira(dashboard?.totalSales || '0')}
                        fullValue={formatFullNaira(dashboard?.totalSales || '0')}
                        icon={DollarSign}
                        gradient="from-emerald-500 to-teal-600"
                        accent="text-emerald-700"
                        subtitle={`${dashboard?.totalTransactions || 0} completed orders`}
                        trend={salesTrend > 0 ? 'up' : salesTrend < 0 ? 'down' : 'neutral'}
                        trendValue={`${Math.abs(salesTrend).toFixed(1)}% vs yesterday`}
                    />

                    <MetricKpiCard
                        label="Average Basket Value"
                        value={formatNaira(dashboard?.averageBasket || '0')}
                        fullValue={formatFullNaira(dashboard?.averageBasket || '0')}
                        icon={ShoppingCart}
                        gradient="from-blue-500 to-indigo-600"
                        accent="text-blue-700"
                        subtitle="Average spend per sale"
                        badge="Avg Order"
                    />

                    <MetricKpiCard
                        label="Active Shifts"
                        value={String(dashboard?.activeShifts || 0)}
                        icon={Users}
                        gradient="from-amber-500 to-orange-600"
                        accent="text-amber-700"
                        subtitle="Open cashier sessions"
                        badge="Shifts"
                    />

                    <MetricKpiCard
                        label="Total Discounts Given"
                        value={`-${formatNaira(dashboard?.totalDiscount || '0')}`}
                        fullValue={formatFullNaira(dashboard?.totalDiscount || '0')}
                        icon={Tag}
                        gradient="from-purple-500 to-violet-600"
                        accent="text-purple-700"
                        subtitle="Deductions today"
                        badge="Discount"
                    />
                </div>

                {/* 5. INTERACTIVE HOURLY REVENUE ACTIVITY CHART (NEW) */}
                {hourlySales.length > 0 && hasData && (
                    <motion.div
                        initial={{ opacity: 0, y: 15 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-white rounded-2xl border border-slate-200/90 shadow-sm p-5"
                    >
                        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
                            <div>
                                <div className="flex items-center gap-2">
                                    <Clock size={16} className="text-emerald-600" />
                                    <h3 className="font-bold text-slate-800 text-sm">
                                        Today's Hourly Sales Velocity
                                    </h3>
                                    {peakHour && peakHour.revenue > 0 && (
                                        <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 flex items-center gap-1">
                                            <Flame size={11} className="text-amber-500" />
                                            Peak Rush: {peakHour.hour}:00 ({formatNaira(peakHour.revenue)})
                                        </span>
                                    )}
                                </div>
                                <p className="text-xs text-slate-400 mt-0.5">
                                    Real-time transaction volume across 24 operating hours
                                </p>
                            </div>
                            <span className="text-xs text-slate-500 font-medium">
                                Hover over any bar to inspect exact revenue
                            </span>
                        </div>

                        {/* Visual Bar Chart */}
                        <div className="h-28 flex items-end gap-1.5 pt-4 border-b border-slate-100">
                            {hourlySales.map((h) => {
                                const heightPct = maxHourRevenue > 0 ? (h.revenue / maxHourRevenue) * 100 : 0;
                                const isPeak = peakHour && peakHour.hour === h.hour && h.revenue > 0;
                                const isHovered = activeHourlyBar === h.hour;

                                return (
                                    <div
                                        key={h.hour}
                                        onMouseEnter={() => setActiveHourlyBar(h.hour)}
                                        onMouseLeave={() => setActiveHourlyBar(null)}
                                        className="flex-1 flex flex-col items-center h-full justify-end group cursor-pointer relative"
                                    >
                                        {/* Hover Tooltip */}
                                        {isHovered && (
                                            <div className="absolute -top-12 z-20 bg-slate-900 text-white text-[11px] font-semibold py-1 px-2.5 rounded-lg shadow-lg whitespace-nowrap pointer-events-none">
                                                {h.hour}:00 — {formatFullNaira(h.revenue)} ({h.count} sales)
                                            </div>
                                        )}

                                        <motion.div
                                            initial={{ height: 0 }}
                                            animate={{ height: `${Math.max(6, heightPct)}%` }}
                                            transition={{ duration: 0.5, ease: 'easeOut' }}
                                            className={clsx(
                                                'w-full rounded-t-md transition-all',
                                                isPeak
                                                    ? 'bg-gradient-to-t from-emerald-600 to-teal-400'
                                                    : h.revenue > 0
                                                    ? 'bg-emerald-500/80 group-hover:bg-emerald-600'
                                                    : 'bg-slate-100'
                                            )}
                                        />
                                    </div>
                                );
                            })}
                        </div>

                        {/* X-Axis Hour Labels */}
                        <div className="flex justify-between text-[10px] font-bold text-slate-400 mt-2 px-1">
                            <span>12 AM</span>
                            <span>4 AM</span>
                            <span>8 AM</span>
                            <span>12 PM</span>
                            <span>4 PM</span>
                            <span>8 PM</span>
                            <span>11 PM</span>
                        </div>
                    </motion.div>
                )}

                {/* 6. OPERATIONAL HEALTH PROGRESS RINGS */}
                {(canViewBranches || canViewStaff || canViewTerminals) && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {canViewBranches && (
                            <CircularProgressCard
                                label="Branch Network Health"
                                value={`${activeBranches} / ${totalBranches}`}
                                subValue={`${totalBranches - activeBranches} inactive branches`}
                                icon={Building2}
                                gradient="from-blue-500 to-indigo-600"
                                progress={branchHealth}
                                accent="text-blue-700"
                                onClick={() => navigate('/branches')}
                            />
                        )}
                        {canViewStaff && (
                            <CircularProgressCard
                                label="Active Staff Members"
                                value={`${activeStaff} / ${totalStaff}`}
                                subValue={`${totalStaff - activeStaff} inactive / off-shift`}
                                icon={Users}
                                gradient="from-purple-500 to-violet-600"
                                progress={staffActive}
                                accent="text-purple-700"
                                onClick={() => navigate('/staff')}
                            />
                        )}
                        {canViewTerminals && (
                            <CircularProgressCard
                                label="POS Terminals Online"
                                value={`${onlineTerminals} / ${activeTerminals || 0}`}
                                subValue={`${activeTerminals - onlineTerminals} offline in last 10m`}
                                icon={Monitor}
                                gradient="from-teal-500 to-emerald-600"
                                progress={terminalOnline}
                                accent="text-teal-700"
                                onClick={() => navigate('/settings/terminals')}
                            />
                        )}
                    </div>
                )}

                {/* 7. SPLIT TWO COLUMN LAYOUT: PAYMENT CHANNELS & TODAY'S PERFORMANCE */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                    
                    {/* LEFT (2 Columns): Payment Channels Breakdown */}
                    <div className="lg:col-span-2">
                        <div className="bg-white rounded-2xl border border-slate-200/90 shadow-sm overflow-hidden">
                            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 rounded-xl bg-emerald-50 text-emerald-600 ring-1 ring-emerald-100">
                                        <BarChart3 className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <h3 className="font-bold text-slate-800 text-sm">
                                            Payment Channels & Settlement
                                        </h3>
                                        <p className="text-xs text-slate-400">
                                            Distribution of accepted payment instruments
                                        </p>
                                    </div>
                                </div>
                                <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-slate-100 text-slate-600">
                                    {dashboard?.paymentBreakdown?.length || 0} methods used
                                </span>
                            </div>

                            <div className="p-5">
                                {hasData && dashboard?.paymentBreakdown?.length > 0 ? (
                                    <div className="space-y-3">
                                        {dashboard.paymentBreakdown.map((p: any) => (
                                            <PaymentChannelRow
                                                key={p.method}
                                                method={p.method}
                                                icon={
                                                    p.method === 'cash'
                                                        ? Banknote
                                                        : p.method === 'pos_card'
                                                        ? CreditCard
                                                        : p.method === 'bank_transfer'
                                                        ? Smartphone
                                                        : DollarSign
                                                }
                                                total={parseFloat(p.total || '0')}
                                                grandTotal={grandTotal}
                                                count={p.count || 0}
                                                color={
                                                    p.method === 'cash'
                                                        ? 'text-emerald-600'
                                                        : p.method === 'pos_card'
                                                        ? 'text-purple-600'
                                                        : p.method === 'bank_transfer'
                                                        ? 'text-blue-600'
                                                        : 'text-amber-600'
                                                }
                                            />
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-center py-12">
                                        <div className="w-14 h-14 bg-slate-50 rounded-2xl flex items-center justify-center mx-auto mb-3 border border-slate-100">
                                            <Activity size={24} className="text-slate-300" />
                                        </div>
                                        <p className="text-sm font-semibold text-slate-700">
                                            No sales recorded for this date
                                        </p>
                                        <p className="text-xs text-slate-400 mt-1 max-w-sm mx-auto">
                                            Select another date or switch branches above to inspect transaction history.
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* RIGHT (1 Column): Today's Performance Breakdown */}
                    <div className="space-y-4">
                        <div className="bg-white rounded-2xl border border-slate-200/90 shadow-sm overflow-hidden">
                            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                                <h3 className="font-bold text-slate-800 text-sm">
                                    Today's Vital Statistics
                                </h3>
                                <Badge variant="neutral" size="sm">
                                    Summary
                                </Badge>
                            </div>

                            <div className="p-5 space-y-3.5">
                                <div className="flex items-center justify-between pb-3 border-b border-slate-100">
                                    <div className="flex items-center gap-2.5">
                                        <div className="p-2 rounded-lg bg-blue-50 text-blue-600">
                                            <Receipt size={15} />
                                        </div>
                                        <div>
                                            <span className="text-xs font-semibold text-slate-700 block">
                                                VAT / Tax Collected
                                            </span>
                                            <span className="text-[11px] text-slate-400">Government remittance</span>
                                        </div>
                                    </div>
                                    <span className="text-sm font-bold text-blue-700">
                                        {formatFullNaira(dashboard?.totalTax || '0')}
                                    </span>
                                </div>

                                <div className="flex items-center justify-between pb-3 border-b border-slate-100">
                                    <div className="flex items-center gap-2.5">
                                        <div className="p-2 rounded-lg bg-emerald-50 text-emerald-600">
                                            <ShoppingCart size={15} />
                                        </div>
                                        <div>
                                            <span className="text-xs font-semibold text-slate-700 block">
                                                Average Items / Sale
                                            </span>
                                            <span className="text-[11px] text-slate-400">Basket product density</span>
                                        </div>
                                    </div>
                                    <span className="text-sm font-bold text-slate-800">
                                        {dashboard?.avgItemsPerSale || '0'} units
                                    </span>
                                </div>

                                <div className="flex items-center justify-between pb-3 border-b border-slate-100">
                                    <div className="flex items-center gap-2.5">
                                        <div className="p-2 rounded-lg bg-rose-50 text-rose-600">
                                            <AlertCircle size={15} />
                                        </div>
                                        <div>
                                            <span className="text-xs font-semibold text-slate-700 block">
                                                Voided Transactions
                                            </span>
                                            <span className="text-[11px] text-slate-400">Canceled before completion</span>
                                        </div>
                                    </div>
                                    <span
                                        className={clsx(
                                            'text-sm font-bold',
                                            dashboard?.voidCount > 0 ? 'text-rose-600' : 'text-slate-400'
                                        )}
                                    >
                                        {dashboard?.voidCount || 0}
                                    </span>
                                </div>

                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2.5">
                                        <div className="p-2 rounded-lg bg-amber-50 text-amber-600">
                                            <RefreshCw size={15} />
                                        </div>
                                        <div>
                                            <span className="text-xs font-semibold text-slate-700 block">
                                                Refunded Orders
                                            </span>
                                            <span className="text-[11px] text-slate-400">Processed returns</span>
                                        </div>
                                    </div>
                                    <span
                                        className={clsx(
                                            'text-sm font-bold',
                                            dashboard?.refundCount > 0 ? 'text-amber-600' : 'text-slate-400'
                                        )}
                                    >
                                        {dashboard?.refundCount || 0}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* System status pill */}
                        <div className="p-4 rounded-2xl bg-gradient-to-r from-emerald-500/10 via-teal-500/10 to-slate-100 border border-emerald-200/60 flex items-center justify-between">
                            <div className="flex items-center gap-2.5">
                                <div className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-ping" />
                                <div>
                                    <p className="text-xs font-bold text-emerald-900">
                                        Cloud Engine Active
                                    </p>
                                    <p className="text-[11px] text-emerald-700/80">
                                        Sub-10ms aggregated reporting pipeline
                                    </p>
                                </div>
                            </div>
                            <CheckCircle2 size={18} className="text-emerald-600" />
                        </div>
                    </div>
                </div>

                {/* 8. TOP PERFORMING PRODUCTS & RECENT LIVE SALES FEED */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                    {/* Top Selling Products Leaderboard */}
                    <div className="bg-white rounded-2xl border border-slate-200/90 shadow-sm overflow-hidden flex flex-col">
                        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-xl bg-amber-50 text-amber-600 ring-1 ring-amber-100">
                                    <Sparkles className="w-5 h-5" />
                                </div>
                                <div>
                                    <h3 className="font-bold text-slate-800 text-sm">
                                        Top Selling Products Today
                                    </h3>
                                    <p className="text-xs text-slate-400">
                                        Highest revenue generated items on selected date
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={() => navigate('/products')}
                                className="text-xs font-semibold text-emerald-600 hover:text-emerald-700 flex items-center gap-1 group"
                                type="button"
                            >
                                All Products
                                <ArrowUpRight size={13} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                            </button>
                        </div>

                        <div className="p-5 flex-1 flex flex-col justify-center">
                            {dashboard?.topProducts && dashboard.topProducts.length > 0 ? (
                                <div className="space-y-4">
                                    {dashboard.topProducts.map((prod: any, idx: number) => {
                                        const maxRev = parseFloat(dashboard.topProducts[0]?.revenue || '1');
                                        const prodRev = parseFloat(prod.revenue || '0');
                                        const pct = maxRev > 0 ? Math.min(100, Math.round((prodRev / maxRev) * 100)) : 0;

                                        return (
                                            <div key={prod.productId} className="group">
                                                <div className="flex items-center justify-between mb-1.5 text-xs">
                                                    <div className="flex items-center gap-2 min-w-0">
                                                        <span className={clsx(
                                                            'w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0',
                                                            idx === 0 ? 'bg-amber-100 text-amber-800 ring-1 ring-amber-200' :
                                                            idx === 1 ? 'bg-slate-200 text-slate-700' :
                                                            idx === 2 ? 'bg-amber-50 text-amber-700' :
                                                            'bg-slate-100 text-slate-500'
                                                        )}>
                                                            #{idx + 1}
                                                        </span>
                                                        <span className="font-semibold text-slate-800 truncate" title={prod.productName}>
                                                            {prod.productName}
                                                        </span>
                                                        {prod.sku && (
                                                            <span className="text-[10px] text-slate-400 font-mono hidden sm:inline">
                                                                ({prod.sku})
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="text-right flex-shrink-0 ml-2">
                                                        <span className="font-bold text-slate-900 block">
                                                            {formatFullNaira(prod.revenue)}
                                                        </span>
                                                        <span className="text-[10px] text-slate-400">
                                                            {prod.quantity} units sold
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden">
                                                    <div
                                                        className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full transition-all duration-500"
                                                        style={{ width: `${pct}%` }}
                                                    />
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className="text-center py-8">
                                    <Package className="w-10 h-10 text-slate-200 mx-auto mb-2" />
                                    <p className="text-xs font-semibold text-slate-600">No product sales yet</p>
                                    <p className="text-[11px] text-slate-400 mt-0.5">Top performing items will automatically display here as orders are placed.</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Recent Transactions Feed */}
                    <div className="bg-white rounded-2xl border border-slate-200/90 shadow-sm overflow-hidden flex flex-col">
                        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-xl bg-blue-50 text-blue-600 ring-1 ring-blue-100">
                                    <Receipt className="w-5 h-5" />
                                </div>
                                <div>
                                    <h3 className="font-bold text-slate-800 text-sm">
                                        Recent Live Transactions
                                    </h3>
                                    <p className="text-xs text-slate-400">
                                        Latest completed checkout activity
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={() => navigate('/sales')}
                                className="text-xs font-semibold text-emerald-600 hover:text-emerald-700 flex items-center gap-1 group"
                                type="button"
                            >
                                Sales History
                                <ArrowUpRight size={13} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                            </button>
                        </div>

                        <div className="p-5 flex-1 flex flex-col justify-center">
                            {dashboard?.recentSales && dashboard.recentSales.length > 0 ? (
                                <div className="divide-y divide-slate-100">
                                    {dashboard.recentSales.map((sale: any) => {
                                        const saleDate = new Date(sale.createdAt);
                                        const timeStr = saleDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

                                        return (
                                            <div key={sale.id} className="py-2.5 first:pt-0 last:pb-0 flex items-center justify-between gap-3">
                                                <div className="min-w-0">
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-xs font-bold text-slate-800">
                                                            #{sale.orderNumber}
                                                        </span>
                                                        <span className={clsx(
                                                            'text-[10px] font-semibold px-2 py-0.5 rounded-full capitalize',
                                                            sale.status === 'completed' ? 'bg-emerald-50 text-emerald-700' :
                                                            sale.status === 'voided' ? 'bg-rose-50 text-rose-700' :
                                                            sale.status === 'refunded' ? 'bg-amber-50 text-amber-700' :
                                                            'bg-slate-100 text-slate-600'
                                                        )}>
                                                            {sale.status}
                                                        </span>
                                                    </div>
                                                    <p className="text-[11px] text-slate-400 mt-0.5 truncate">
                                                        {sale.cashierName ? `Cashier: ${sale.cashierName}` : 'Self Checkout'} • {sale.customerName || 'Walk-in'} • {timeStr}
                                                    </p>
                                                </div>

                                                <div className="text-right flex-shrink-0">
                                                    <span className="text-xs font-bold text-slate-900 block">
                                                        {formatFullNaira(sale.grandTotal)}
                                                    </span>
                                                    <span className="text-[10px] text-slate-400 capitalize">
                                                        {sale.saleType || 'retail'}
                                                    </span>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className="text-center py-8">
                                    <Receipt className="w-10 h-10 text-slate-200 mx-auto mb-2" />
                                    <p className="text-xs font-semibold text-slate-600">No recent transactions</p>
                                    <p className="text-[11px] text-slate-400 mt-0.5">Orders processed on the POS will stream here in real time.</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* POS SELECTOR MODAL */}
            <AnimatePresence>
                {showPosSelector && (
                    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-6 border border-slate-200"
                        >
                            <div className="flex items-center justify-between mb-3">
                                <div className="p-2.5 rounded-xl bg-emerald-50 text-emerald-700">
                                    <ShoppingCart size={22} />
                                </div>
                                <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">
                                    Terminal Selection
                                </span>
                            </div>

                            <h3 className="text-xl font-extrabold text-slate-900">
                                Select Operating Interface
                            </h3>
                            <p className="text-xs text-slate-500 mt-1 mb-5">
                                Choose which point-of-sale layout you want to operate on this station.
                            </p>

                            <div className="space-y-3">
                                <button
                                    onClick={() => {
                                        setShowPosSelector(false);
                                        navigate('/pos');
                                    }}
                                    className="w-full flex items-center gap-4 p-4 rounded-2xl border-2 border-emerald-200 bg-gradient-to-r from-emerald-50/60 to-white hover:border-emerald-500 hover:shadow-md transition-all group text-left"
                                    type="button"
                                >
                                    <div className="p-3 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl shadow-md text-white">
                                        <ShoppingCart className="w-6 h-6" />
                                    </div>
                                    <div className="flex-1">
                                        <h4 className="font-bold text-slate-900 text-sm">Retail Store POS</h4>
                                        <p className="text-xs text-slate-500 mt-0.5">
                                            Barcode scanners, inventory lookups, and fast retail checkout.
                                        </p>
                                    </div>
                                    <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-emerald-600 group-hover:translate-x-1 transition-all" />
                                </button>

                                <button
                                    onClick={() => {
                                        setShowPosSelector(false);
                                        navigate('/bom-pos');
                                    }}
                                    className="w-full flex items-center gap-4 p-4 rounded-2xl border-2 border-amber-200 bg-gradient-to-r from-amber-50/60 to-white hover:border-amber-500 hover:shadow-md transition-all group text-left"
                                    type="button"
                                >
                                    <div className="p-3 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl shadow-md text-white">
                                        <Utensils className="w-6 h-6" />
                                    </div>
                                    <div className="flex-1">
                                        <h4 className="font-bold text-slate-900 text-sm">Restaurant / Menu POS</h4>
                                        <p className="text-xs text-slate-500 mt-0.5">
                                            Recipe BOM deduction, kitchen display order routing, and tables.
                                        </p>
                                    </div>
                                    <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-amber-600 group-hover:translate-x-1 transition-all" />
                                </button>
                            </div>

                            <button
                                onClick={() => setShowPosSelector(false)}
                                className="mt-5 w-full py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold transition-all"
                                type="button"
                            >
                                Cancel
                            </button>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
}